/**
 * Serializable, UI-independent connector routing.  This module intentionally
 * has no React or DOM dependencies so it can run in a Web Worker.
 */
export type RoutePoint = { x: number; y: number };
export type RoutingRect = { x: number; y: number; w: number; h: number };
export type RoutingMarker = { itemId: number; itemIds?: number[]; x: number; y: number; radius: number };
export type CanvasSide = 'top' | 'right' | 'bottom' | 'left';
export type LineStyle = 'straight' | 'one-bend' | 'two-bend' | 'three-bend' | 'four-bend' | 'five-bend' | 'six-bend' | 'seven-bend';

/** Shared default used by interactive, generated, and print routes. */
export function defaultLineStyle(anchor: RoutePoint, target: RoutePoint): LineStyle {
  return Math.abs(anchor.x - target.x) < 1 || Math.abs(anchor.y - target.y) < 1
    ? 'straight'
    : 'one-bend';
}
export type RouteStatus = 'connected' | 'missing-coordinates' | 'no-safe-route';

export interface RoutingItem {
  id: number;
  sortOrder: number;
  position: RoutingRect;
  anchorSide?: CanvasSide;
  anchorOffset?: number;
  lineStyle?: LineStyle;
  bendX?: number;
  bendY?: number;
  bend2X?: number;
  bend2Y?: number;
  bend3X?: number;
  bend3Y?: number;
  bend4X?: number;
  bend4Y?: number;
  bend5X?: number;
  bend5Y?: number;
  bend6X?: number;
  bend6Y?: number;
  bend7X?: number;
  bend7Y?: number;
  connectorVisible?: boolean;
  hasCoordinates: boolean;
}

export interface RoutingSnapshot {
  canvasW: number;
  canvasH: number;
  footerClearance: number;
  connectorClearance: number;
  connectorMinGap: number;
  connectorStrokeWidth: number;
  titleBounds: RoutingRect;
  furnitureBounds: RoutingRect[];
  items: RoutingItem[];
  markers: RoutingMarker[];
  existingRoutes?: Array<{ itemId: number; points: RoutePoint[] }>;
}

export interface RouteResult {
  itemId: number;
  status: RouteStatus;
  points: RoutePoint[];
  reason?: string;
}

type Segment = { start: RoutePoint; end: RoutePoint; horizontal: boolean };

const distance = (a: RoutePoint, b: RoutePoint) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
const equal = (a: RoutePoint, b: RoutePoint) => Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;
const segments = (points: RoutePoint[]): Segment[] => points.slice(1).reduce<Segment[]>((out, end, i) => {
  const start = points[i];
  if (equal(start, end)) return out;
  out.push({ start, end, horizontal: Math.abs(start.y - end.y) < 0.5 });
  return out;
}, []);
/** Insert transient elbow points so every rendered segment is horizontal or vertical. */
export const orthogonalizeRoute = (points: RoutePoint[]) => {
  const out: RoutePoint[] = points.length ? [points[0]] : [];
  points.slice(1).forEach((point) => {
    const previous = out[out.length - 1];
    if (!previous) { out.push(point); return; }
    if (Math.abs(previous.x - point.x) >= 0.5 && Math.abs(previous.y - point.y) >= 0.5) {
      const horizontalFirst = Math.abs(point.x - previous.x) >= Math.abs(point.y - previous.y);
      out.push(horizontalFirst ? { x: point.x, y: previous.y } : { x: previous.x, y: point.y });
    }
    out.push(point);
  });
  return out;
};
const dedupe = (points: RoutePoint[]) => orthogonalizeRoute(points).reduce<RoutePoint[]>((out, point) => {
  const previous = out[out.length - 1];
  if (!previous || !equal(previous, point)) {
    out.push(point);
    while (out.length >= 3) {
      const a = out[out.length - 3];
      const b = out[out.length - 2];
      const c = out[out.length - 1];
      if ((Math.abs(a.y - b.y) < 0.5 && Math.abs(b.y - c.y) < 0.5)
        || (Math.abs(a.x - b.x) < 0.5 && Math.abs(b.x - c.x) < 0.5)) out.splice(out.length - 2, 1);
      else break;
    }
  }
  return out;
}, []);

const expand = (r: RoutingRect, p: number): RoutingRect => ({ x: r.x - p, y: r.y - p, w: r.w + p * 2, h: r.h + p * 2 });
function segmentHitsRect(a: RoutePoint, b: RoutePoint, rect: RoutingRect): boolean {
  if (Math.abs(a.x - b.x) >= 0.5 && Math.abs(a.y - b.y) >= 0.5) return true;
  if (Math.abs(a.y - b.y) < 0.5) {
    return a.y >= rect.y && a.y <= rect.y + rect.h
      && Math.max(a.x, b.x) >= rect.x && Math.min(a.x, b.x) <= rect.x + rect.w;
  }
  return a.x >= rect.x && a.x <= rect.x + rect.w
    && Math.max(a.y, b.y) >= rect.y && Math.min(a.y, b.y) <= rect.y + rect.h;
}

function segmentHitsCircle(a: RoutePoint, b: RoutePoint, marker: RoutingMarker): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denominator = dx * dx + dy * dy;
  const t = denominator ? Math.max(0, Math.min(1, ((marker.x - a.x) * dx + (marker.y - a.y) * dy) / denominator)) : 0;
  return Math.hypot(marker.x - (a.x + t * dx), marker.y - (a.y + t * dy)) <= marker.radius;
}

function parallelConflict(candidate: Segment, existing: RoutePoint[][], gap: number): boolean {
  return existing.some((route) => segments(route).some((other) => {
    if (candidate.horizontal !== other.horizontal) return false;
    const axisDistance = candidate.horizontal
      ? Math.abs(candidate.start.y - other.start.y)
      : Math.abs(candidate.start.x - other.start.x);
    if (axisDistance < gap) {
      const a0 = candidate.horizontal ? candidate.start.x : candidate.start.y;
      const a1 = candidate.horizontal ? candidate.end.x : candidate.end.y;
      const b0 = candidate.horizontal ? other.start.x : other.start.y;
      const b1 = candidate.horizontal ? other.end.x : other.end.y;
      return Math.min(Math.max(a0, a1), Math.max(b0, b1)) - Math.max(Math.min(a0, a1), Math.min(b0, b1)) > 2;
    }
    return false;
  }));
}

function anchorOf(item: RoutingItem, canvasW: number, canvasH: number): { point: RoutePoint; side: CanvasSide } {
  const savedSide = item.anchorSide;
  const side = savedSide ?? (() => {
    const distances: Array<[CanvasSide, number]> = [
      ['left', item.position.x],
      ['right', canvasW - item.position.x - item.position.w],
      ['top', item.position.y],
      ['bottom', canvasH - item.position.y - item.position.h],
    ];
    const nearest = distances.reduce((a, b) => b[1] < a[1] ? b : a)[0];
    return nearest === 'left' ? 'right' : nearest === 'right' ? 'left' : nearest === 'top' ? 'bottom' : 'top';
  })();
  // Card connectors always terminate at the centre of the selected side.
  // `anchorOffset` remains in the persisted shape for backwards compatibility
  // but is intentionally ignored by the router.
  const offset = 0.5;
  if (side === 'top') return { side, point: { x: item.position.x + item.position.w * offset, y: item.position.y } };
  if (side === 'right') return { side, point: { x: item.position.x + item.position.w, y: item.position.y + item.position.h * offset } };
  if (side === 'left') return { side, point: { x: item.position.x, y: item.position.y + item.position.h * offset } };
  return { side, point: { x: item.position.x + item.position.w * offset, y: item.position.y + item.position.h } };
}

const departure = (point: RoutePoint, side: CanvasSide): RoutePoint => {
  const stub = 12;
  if (side === 'left') return { x: point.x - stub, y: point.y };
  if (side === 'right') return { x: point.x + stub, y: point.y };
  if (side === 'top') return { x: point.x, y: point.y - stub };
  return { x: point.x, y: point.y + stub };
};

/**
 * Connectors terminate at the marker centre.  The marker is painted after the
 * connector polyline, so its opaque fill keeps the line from obscuring the
 * number while still making the connection visibly originate at the centre.
 */
function targetFor(_anchor: RoutePoint, marker: RoutingMarker): RoutePoint {
  return { x: marker.x, y: marker.y };
}

function routeSafe(
  points: RoutePoint[], itemId: number, anchor: RoutePoint, side: CanvasSide,
  rectangles: RoutingRect[], markers: RoutingMarker[], existing: RoutePoint[][], gap: number, canvasW: number, canvasH: number,
): boolean {
  const route = dedupe(points);
  if (route.length < 2 || route.some((p) => p.x < 1 || p.y < 1 || p.x > canvasW - 1 || p.y > canvasH - 1)) return false;
  const first = route[1];
  if (side === 'left' && !(Math.abs(first.y - anchor.y) < 0.5 && first.x < anchor.x)) return false;
  if (side === 'right' && !(Math.abs(first.y - anchor.y) < 0.5 && first.x > anchor.x)) return false;
  if (side === 'top' && !(Math.abs(first.x - anchor.x) < 0.5 && first.y < anchor.y)) return false;
  if (side === 'bottom' && !(Math.abs(first.x - anchor.x) < 0.5 && first.y > anchor.y)) return false;
  const routeSegments = segments(route);
  if (routeSegments.some((segment) => rectangles.some((rect) => segmentHitsRect(segment.start, segment.end, rect)))) return false;
  if (routeSegments.some((segment) => markers.some((marker) => !(marker.itemIds ?? [marker.itemId]).includes(itemId) && segmentHitsCircle(segment.start, segment.end, marker)))) return false;
  return !routeSegments.some((segment) => parallelConflict(segment, existing, gap));
}

function visibilityRoute(
  snapshot: RoutingSnapshot, item: RoutingItem, anchor: RoutePoint, target: RoutePoint, side: CanvasSide, existing: RoutePoint[][],
): RoutePoint[] {
  const padding = snapshot.connectorClearance + snapshot.connectorStrokeWidth / 2 + 1;
  const rectangles = [
    ...snapshot.items.filter((candidate) => candidate.id !== item.id).map((candidate) => expand(candidate.position, padding)),
    ...snapshot.furnitureBounds.map((rect) => expand(rect, padding)),
  ];
  const markers = snapshot.markers.filter((marker) => !(marker.itemIds ?? [marker.itemId]).includes(item.id))
    .map((marker) => ({ ...marker, radius: marker.radius + padding }));
  const start = departure(anchor, side);
  const xs = new Set<number>([2, snapshot.canvasW - 2, anchor.x, start.x, target.x]);
  const ys = new Set<number>([2, snapshot.canvasH - 2, anchor.y, start.y, target.y]);
  rectangles.forEach((r) => {
    [r.x - 1, r.x, r.x + r.w, r.x + r.w + 1].forEach((v) => xs.add(Math.max(2, Math.min(snapshot.canvasW - 2, v))));
    [r.y - 1, r.y, r.y + r.h, r.y + r.h + 1].forEach((v) => ys.add(Math.max(2, Math.min(snapshot.canvasH - 2, v))));
  });
  markers.forEach((m) => {
    [m.x - m.radius - 1, m.x - m.radius, m.x + m.radius, m.x + m.radius + 1].forEach((v) => xs.add(Math.max(2, Math.min(snapshot.canvasW - 2, v))));
    [m.y - m.radius - 1, m.y - m.radius, m.y + m.radius, m.y + m.radius + 1].forEach((v) => ys.add(Math.max(2, Math.min(snapshot.canvasH - 2, v))));
  });
  existing.flatMap(segments).forEach((segment) => {
    const corridor = snapshot.connectorMinGap + 1;
    if (segment.horizontal) [segment.start.y - corridor, segment.start.y + corridor].forEach((v) => ys.add(Math.max(2, Math.min(snapshot.canvasH - 2, v))));
    else [segment.start.x - corridor, segment.start.x + corridor].forEach((v) => xs.add(Math.max(2, Math.min(snapshot.canvasW - 2, v))));
  });
  const xValues = Array.from(xs).filter(Number.isFinite).sort((a, b) => a - b);
  const yValues = Array.from(ys).filter(Number.isFinite).sort((a, b) => a - b);
  const startX = xValues.findIndex((v) => Math.abs(v - start.x) < 0.5);
  const startY = yValues.findIndex((v) => Math.abs(v - start.y) < 0.5);
  const endX = xValues.findIndex((v) => Math.abs(v - target.x) < 0.5);
  const endY = yValues.findIndex((v) => Math.abs(v - target.y) < 0.5);
  if ([startX, startY, endX, endY].some((v) => v < 0)) return [];
  const pointAt = (x: number, y: number) => ({ x: xValues[x], y: yValues[y] });
  const inside = (p: RoutePoint, r: RoutingRect) => p.x > r.x && p.x < r.x + r.w && p.y > r.y && p.y < r.y + r.h;
  const blockedPoint = (p: RoutePoint, endpoint: boolean) => !endpoint && (rectangles.some((r) => inside(p, r)) || markers.some((m) => Math.hypot(p.x - m.x, p.y - m.y) < m.radius));
  const blockedSegment = (a: RoutePoint, b: RoutePoint) => {
    if (Math.abs(a.x - b.x) >= 0.5 && Math.abs(a.y - b.y) >= 0.5) return true;
    if (rectangles.some((rect) => segmentHitsRect(a, b, rect))) return true;
    if (markers.some((marker) => segmentHitsCircle(a, b, marker))) return true;
    return parallelConflict({ start: a, end: b, horizontal: Math.abs(a.y - b.y) < 0.5 }, existing, snapshot.connectorMinGap);
  };
  type Search = { x: number; y: number; dir: number; g: number; f: number; key: string };
  const key = (x: number, y: number, d: number) => `${x}:${y}:${d}`;
  const open: Search[] = [];
  const push = (state: Search) => { open.push(state); let i = open.length - 1; while (i > 0) { const p = Math.floor((i - 1) / 2); if (open[p].f <= open[i].f) break; [open[p], open[i]] = [open[i], open[p]]; i = p; } };
  const pop = (): Search | undefined => { if (!open.length) return undefined; const first = open[0]; const last = open.pop(); if (last && open.length) { open[0] = last; let i = 0; while (true) { const l = i * 2 + 1; const r = l + 1; let s = i; if (l < open.length && open[l].f < open[s].f) s = l; if (r < open.length && open[r].f < open[s].f) s = r; if (s === i) break; [open[i], open[s]] = [open[s], open[i]]; i = s; } } return first; };
  const startKey = key(startX, startY, -1);
  const best = new Map<string, number>([[startKey, 0]]);
  const previous = new Map<string, string>();
  const heuristic = (x: number, y: number) => Math.abs(xValues[x] - target.x) + Math.abs(yValues[y] - target.y);
  push({ x: startX, y: startY, dir: -1, g: 0, f: heuristic(startX, startY), key: startKey });
  const directions: Array<[number, number]> = [[1, 0], [0, 1], [-1, 0], [0, -1]];
  let found: Search | undefined;
  let iterations = 0;
  while (open.length && iterations < 120000) {
    const current = pop();
    if (!current || current.g !== best.get(current.key)) continue;
    iterations += 1;
    if (current.x === endX && current.y === endY) { found = current; break; }
    directions.forEach(([dx, dy], dir) => {
      const nx = current.x + dx; const ny = current.y + dy;
      if (nx < 0 || ny < 0 || nx >= xValues.length || ny >= yValues.length) return;
      const a = pointAt(current.x, current.y); const b = pointAt(nx, ny);
      if (blockedPoint(b, nx === endX && ny === endY) || blockedSegment(a, b)) return;
      const nextCost = current.g + distance(a, b) + (current.dir >= 0 && current.dir !== dir ? 10 : 0);
      const nextKey = key(nx, ny, dir);
      if (nextCost >= (best.get(nextKey) ?? Number.POSITIVE_INFINITY)) return;
      best.set(nextKey, nextCost); previous.set(nextKey, current.key);
      push({ x: nx, y: ny, dir, g: nextCost, f: nextCost + heuristic(nx, ny), key: nextKey });
    });
  }
  const perimeter: RoutePoint[][] = [
    [anchor, start, { x: 2, y: start.y }, { x: 2, y: target.y }, target],
    [anchor, start, { x: snapshot.canvasW - 2, y: start.y }, { x: snapshot.canvasW - 2, y: target.y }, target],
    [anchor, start, { x: start.x, y: 2 }, { x: target.x, y: 2 }, target],
    [anchor, start, { x: start.x, y: snapshot.canvasH - snapshot.footerClearance - 2 }, { x: target.x, y: snapshot.canvasH - snapshot.footerClearance - 2 }, target],
  ];
  const safePerimeter = () => perimeter
    .map(dedupe)
    .find((candidate) => routeSafe(candidate, item.id, anchor, side, rectangles, markers, existing, snapshot.connectorMinGap, snapshot.canvasW, snapshot.canvasH));

  if (!found) return safePerimeter() ?? [];
  const points: RoutePoint[] = [];
  let currentKey: string | undefined = found.key;
  while (currentKey) { const [x, y] = currentKey.split(':').map(Number); points.push(pointAt(x, y)); currentKey = previous.get(currentKey); }
  points.reverse();
  const route = dedupe([anchor, ...points]);
  if (routeSafe(route, item.id, anchor, side, rectangles, markers, existing, snapshot.connectorMinGap, snapshot.canvasW, snapshot.canvasH)) return route;
  // A visibility-graph path can still be rejected after simplification (for
  // example when a reserved corridor is introduced). Do not give up without
  // trying the deterministic perimeter corridors as a final safe fallback.
  return safePerimeter() ?? [];
}

function preferredRoute(snapshot: RoutingSnapshot, item: RoutingItem, anchor: RoutePoint, target: RoutePoint, side: CanvasSide, existing: RoutePoint[][]): RoutePoint[] {
  const style: LineStyle = item.lineStyle ?? defaultLineStyle(anchor, target);
  const horizontalFirst = side === 'left' || side === 'right';
  const dx = target.x - anchor.x; const dy = target.y - anchor.y;
  const midX = anchor.x + dx * 0.5;
  const midY = anchor.y + dy * 0.5;
  const firstX = anchor.x + dx * 0.33;
  const secondX = anchor.x + dx * 0.66;
  const firstY = anchor.y + dy * 0.33;
  const secondY = anchor.y + dy * 0.66;
  const generatedOne = horizontalFirst ? { x: target.x, y: anchor.y } : { x: anchor.x, y: target.y };
  const generatedTwo = horizontalFirst
    ? [{ x: midX, y: anchor.y }, { x: midX, y: target.y }]
    : [{ x: anchor.x, y: midY }, { x: target.x, y: midY }];
  const generatedThree = horizontalFirst
    ? [{ x: firstX, y: anchor.y }, { x: firstX, y: midY }, { x: target.x, y: midY }]
    : [{ x: anchor.x, y: firstY }, { x: midX, y: firstY }, { x: midX, y: target.y }];
  const generatedFour = horizontalFirst
    ? [{ x: firstX, y: anchor.y }, { x: firstX, y: secondY }, { x: secondX, y: secondY }, { x: secondX, y: target.y }]
    : [{ x: anchor.x, y: firstY }, { x: secondX, y: firstY }, { x: secondX, y: secondY }, { x: target.x, y: secondY }];
  const savedBends: Array<RoutePoint | undefined> = [
    item.bendX != null && item.bendY != null ? { x: item.bendX, y: item.bendY } : undefined,
    item.bend2X != null && item.bend2Y != null ? { x: item.bend2X, y: item.bend2Y } : undefined,
    item.bend3X != null && item.bend3Y != null ? { x: item.bend3X, y: item.bend3Y } : undefined,
    item.bend4X != null && item.bend4Y != null ? { x: item.bend4X, y: item.bend4Y } : undefined,
    item.bend5X != null && item.bend5Y != null ? { x: item.bend5X, y: item.bend5Y } : undefined,
    item.bend6X != null && item.bend6Y != null ? { x: item.bend6X, y: item.bend6Y } : undefined,
    item.bend7X != null && item.bend7Y != null ? { x: item.bend7X, y: item.bend7Y } : undefined,
  ];
  // For routes longer than four bends, extend the final segment of the
  // existing orthogonal four-bend template with collinear points. Saved
  // coordinates override these generated positions and remain freely
  // draggable in the editor.
  const generatedLong: RoutePoint[] = [...generatedFour];
  const tailStart = generatedFour[generatedFour.length - 1];
  for (let i = 5; i <= 7; i += 1) {
    const fraction = (i - 4) / 4;
    generatedLong.push({
      x: tailStart.x + (target.x - tailStart.x) * fraction,
      y: tailStart.y + (target.y - tailStart.y) * fraction,
    });
  }
  const generatedBends: RoutePoint[] = [
    generatedOne,
    generatedTwo[0], generatedTwo[1],
    generatedThree[0], generatedThree[1], generatedThree[2],
    generatedFour[0], generatedFour[1], generatedFour[2], generatedFour[3],
    generatedLong[4], generatedLong[5], generatedLong[6],
  ];
  const explicitBends: Array<RoutePoint | undefined> = [
    savedBends[0], savedBends[1], savedBends[2], savedBends[3], savedBends[4], savedBends[5], savedBends[6],
  ];
  const bendCount = style === 'straight' ? 0 : style === 'one-bend' ? 1 : style === 'two-bend' ? 2
    : style === 'three-bend' ? 3 : style === 'four-bend' ? 4 : style === 'five-bend' ? 5
      : style === 'six-bend' ? 6 : 7;
  const generatedForCount: RoutePoint[] = bendCount <= 1
    ? [generatedBends[0]]
    : bendCount === 2
      ? [generatedBends[1], generatedBends[2]]
      : bendCount === 3
        ? [generatedBends[3], generatedBends[4], generatedBends[5]]
        : generatedBends.slice(6, 6 + bendCount);
  const points: RoutePoint[] = bendCount === 0
    ? [anchor, target]
    : [anchor, ...Array.from({ length: bendCount }, (_, index) => explicitBends[index] ?? generatedForCount[index]), target];
  const rectangles = [
    ...snapshot.items.filter((candidate) => candidate.id !== item.id).map((candidate) => expand(candidate.position, snapshot.connectorClearance + snapshot.connectorStrokeWidth / 2)),
    ...snapshot.furnitureBounds.map((rect) => expand(rect, snapshot.connectorClearance + snapshot.connectorStrokeWidth / 2)),
  ];
  const markers = snapshot.markers.filter((marker) => marker.itemId !== item.id).map((marker) => ({ ...marker, radius: marker.radius + snapshot.connectorClearance + snapshot.connectorStrokeWidth / 2 }));
  const candidate = dedupe([anchor, departure(anchor, side), ...points.slice(1)]);
  if (routeSafe(candidate, item.id, anchor, side, rectangles, markers, existing, snapshot.connectorMinGap, snapshot.canvasW, snapshot.canvasH)) return candidate;
  return visibilityRoute(snapshot, item, anchor, target, side, existing);
}

export function orderedRoutingItems(snapshot: RoutingSnapshot, targetIds?: number[]): RoutingItem[] {
  const filter = targetIds ? new Set(targetIds) : null;
  // Connector visibility is no longer user-configurable from the line menu.
  // Keep the optional field in the serialisable shape for old drafts, but
  // route every item so legacy `false` values cannot silently disconnect a
  // card after loading.
  return snapshot.items.filter((item) => !filter || filter.has(item.id))
    .sort((a, b) => {
      const aEdge = Math.min(a.position.x, a.position.y, snapshot.canvasW - a.position.x - a.position.w, snapshot.canvasH - a.position.y - a.position.h);
      const bEdge = Math.min(b.position.x, b.position.y, snapshot.canvasW - b.position.x - b.position.w, snapshot.canvasH - b.position.y - b.position.h);
      return bEdge - aEdge || a.sortOrder - b.sortOrder || a.id - b.id;
    });
}

export function routeItem(snapshot: RoutingSnapshot, item: RoutingItem, existing: RoutePoint[][] = []): RouteResult {
  if (!item.hasCoordinates) return { itemId: item.id, status: 'missing-coordinates', points: [], reason: 'latitude/longitude is unavailable' };
  const marker = snapshot.markers.find((candidate) => (candidate.itemIds ?? [candidate.itemId]).includes(item.id));
  if (!marker) return { itemId: item.id, status: 'missing-coordinates', points: [], reason: 'project marker is unavailable' };
  const { point: anchor, side } = anchorOf(item, snapshot.canvasW, snapshot.canvasH);
  const target = targetFor(anchor, marker);
  const route = preferredRoute(snapshot, item, anchor, target, side, existing);
  return route.length > 1
    ? { itemId: item.id, status: 'connected', points: route }
    : { itemId: item.id, status: 'no-safe-route', points: [], reason: 'blocked by cards, markers, protected furniture, or reserved connector corridors' };
}

export function routeSnapshot(snapshot: RoutingSnapshot, targetIds?: number[]): RouteResult[] {
  const items = orderedRoutingItems(snapshot, targetIds);
  const targetSet = targetIds ? new Set(targetIds) : null;
  const existing = targetSet
    ? (snapshot.existingRoutes ?? []).filter((route) => !targetSet.has(route.itemId)).map((route) => route.points)
    : [];
  const results: RouteResult[] = [];
  const routeOwners: Array<{ itemId: number; points: RoutePoint[] }> = [];
  items.forEach((item) => {
    const marker = snapshot.markers.find((candidate) => (candidate.itemIds ?? [candidate.itemId]).includes(item.id));
    const sharesMarker = marker != null && routeOwners.some((owner) =>
      (marker.itemIds ?? [marker.itemId]).includes(owner.itemId));
    // Cards sharing one clustered marker intentionally use a short branch
    // corridor near the marker; they may overlap one another there, while
    // routes to different markers retain the configured parallel gap.
    const routeSnapshotForItem = sharesMarker
      ? { ...snapshot, connectorMinGap: 0 }
      : snapshot;
    const result = routeItem(routeSnapshotForItem, item, existing);
    results.push(result);
    if (result.status === 'connected') {
      existing.push(result.points);
      routeOwners.push({ itemId: item.id, points: result.points });
    }
  });
  return results;
}
